import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Combobox,
  EntryCell,
  EntryGrid,
  EntryRow,
  GridAction,
  GridInput,
  GridSelect,
  Input,
  StatTile,
  Textarea,
  cmpDecimal,
  formatDate,
  formatMoney,
  formatQty,
  formatRate,
  humanizeStatus,
  sumDecimal,
  type ComboboxOption,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { apiSend, useResource } from '../lib/api.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';
import {
  ComboField,
  EntryPanel,
  FieldRow,
  Messages,
  PanelToggle,
  ReadOnlyField,
  SelectField,
  SubmitHint,
  TextField,
  Tiles,
  pickedLine,
  today,
  useAction,
  useAutoRows,
  useDefaultUnits,
  useSubmitShortcut,
} from './document.js';
import { defaultUom } from '../lib/reference-data.js';

/**
 * Inventory, reading and writing PostgreSQL.
 *
 * Six screens over one idea: the stock ledger is the record, and everything else
 * is a projection of it. `stock_balances` is maintained by the same trigger that
 * writes the movement, so "what is on hand" is a primary-key lookup rather than
 * an aggregate — which is what lets the negative-stock guard run on every issue
 * without costing anything.
 *
 * Quantities are shown in the unit the warehouse talks in — Bags — and stored in
 * Kg. Every screen here divides by the pack size to display and multiplies by
 * the conversion factor to post, and the factor used is copied onto the document
 * line so a master-data edit cannot rewrite history.
 */

const R = 'right' as const;

// ── Shared shapes ────────────────────────────────────────────────────────────

interface Warehouse {
  code: string;
  name: string;
  kind: string;
  isDefault: boolean;
  allowNegativeStock: boolean;
  zoneName: string | null;
  inventoryAccount: string | null;
}

interface InventoryRefs {
  warehouses: Warehouse[];
  uoms: Array<{ code: string; name: string; dimension: string; decimals: number }>;
  categories: Array<{ code: string; name: string; parentCode: string | null; depth: number }>;
  reasonCodes: Array<{
    code: string;
    name: string;
    domain: string;
    isResaleable: boolean;
    requiresApproval: boolean;
  }>;
  bins: Array<{ code: string; name: string | null; warehouseCode: string }>;
  users: Array<{ email: string; fullName: string }>;
}

function useRefs() {
  return useResource<InventoryRefs>('/inventory-refs');
}

// ═════════════════════════════════════════════════════════════════════════════
// Product management
// ═════════════════════════════════════════════════════════════════════════════

interface Product {
  sku: string;
  name: string;
  shortName: string | null;
  grade: string | null;
  brand: string | null;
  categoryName: string | null;
  categoryCode: string | null;
  baseUom: string;
  stockUom: string | null;
  purchaseUom: string | null;
  saleUom: string | null;
  packSizeBase: string | null;
  netWeightKg: string | null;
  grossWeightKg: string | null;
  barcode: string | null;
  hsCode: string | null;
  originCountry: string | null;
  trackBatch: boolean;
  trackExpiry: boolean;
  shelfLifeDays: number | null;
  customsDutyPct: string;
  salesTaxCode: string | null;
  salesTaxPct: string | null;
  purchaseTaxCode: string | null;
  purchaseTaxPct: string | null;
  reorderLevel: string | null;
  leadTimeDays: number | null;
  isStockItem: boolean;
  isSellable: boolean;
  isPurchasable: boolean;
  isActive: boolean;
  balanceQty: string;
  balanceValue: string;
  movingAvgRate: string;
  warehouseCount: number;
  lastMovement: string | null;
}

/**
 * What Product Management asks for — which is nine fields fewer than it did.
 *
 * Gone, and why: **grade** duplicated what the item name already says; the
 * **stock, purchase and sale units** were three presentation defaults that made
 * a product look like it needed four units when it needs one; **pack size** was
 * a per-product conversion factor that only ever produced Bag and Ton, both of
 * which have been retired from the register; and **gross weight, HS code,
 * customs duty and origin country** are a customs-declaration block on a screen
 * for stock. The columns all still exist and a value already stored is left
 * alone — `saveProduct` treats an absent key as "unchanged" rather than as
 * "blank", so editing a legacy product through this form does not wipe them.
 *
 * **SKU and barcode are issued, not typed.** Both are identity: a person
 * choosing them is a person inventing a convention, and two conventions is how a
 * catalogue stops sorting. They are absent from the draft on a create and
 * carried on an edit, which is what makes them frozen once issued.
 */
interface ProductDraft {
  /** Blank on a create — the server issues one under the category's prefix. */
  sku: string;
  /** Blank on a create — the server mints an internal EAN-13. */
  barcode: string;
  name: string;
  categoryCode: string;
  brand: string;
  baseUom: string;
  trackBatch: boolean;
  trackExpiry: boolean;
  shelfLifeDays: string;
  leadTimeDays: string;
  isActive: boolean;
  /** Set when editing: the SKU is the identity and does not change. */
  locked: boolean;
}

/**
 * The Category picker's rows: sub-categories only, under their group's heading.
 *
 * **The catalogue is two levels and products hang on the second.** That is the
 * rule `masters.ts` states — `MAX_CATEGORY_DEPTH` is 1, and creating a root
 * says so out loud: "added as a group. Sub-categories go under it, and products
 * go on the sub-categories." The picker did not enforce it. It listed both
 * levels, distinguished only by a `· ` prefix, so a product could be filed on a
 * group that has sub-categories under it — which puts it outside every
 * sub-category its siblings are in, and the category is where a product
 * inherits its four posting accounts from.
 *
 * So the top level becomes a **`group` heading**: visible, because knowing that
 * `Layer Feed` sits under `Poultry` is most of what makes the list legible, and
 * unselectable, because it is not a row.
 *
 * **A childless root is still offered**, under its own name. It is a leaf of the
 * tree as it stands, and refusing it would leave a book part-way through
 * building its catalogue unable to file a product at all. This is the same rule
 * `entryAccounts()` applies to the chart of accounts: offer the leaves, and
 * offer a head only as the parent of the things underneath it.
 */
function categoryOptions(
  categories: Array<{ code: string; name: string; parentCode: string | null; depth: number }>,
): ComboboxOption[] {
  const hasChildren = new Set(
    categories.map((c) => c.parentCode).filter((code): code is string => Boolean(code)),
  );
  const nameOf = new Map(categories.map((c) => [c.code, c.name]));

  return categories
    .filter((c) => Boolean(c.parentCode) || !hasChildren.has(c.code))
    .map((c) => ({
      value: c.code,
      label: c.name,
      hint: c.code,
      group: c.parentCode
        ? (nameOf.get(c.parentCode) ?? c.parentCode)
        : 'No group — top level with no sub-categories yet',
      // So typing the group's name finds everything under it.
      keywords: `${c.parentCode ?? ''} ${c.parentCode ? (nameOf.get(c.parentCode) ?? '') : ''}`,
    }));
}

/**
 * The base unit a new product opens on.
 *
 * The company default from Setup → Company Profile, then whatever the register
 * holds first. It used to be the literal `'KG'`, which is wrong the moment a
 * book trades in something else — and worse than wrong on a `<select>`, because
 * a value matching none of the options silently displays the first one, so the
 * form claimed a unit the draft did not hold.
 */
function blankProduct(): ProductDraft {
  return {
    sku: '',
    barcode: '',
    name: '',
    categoryCode: '',
    brand: '',
    baseUom: defaultUom(),
    trackBatch: true,
    trackExpiry: true,
    shelfLifeDays: '180',
    leadTimeDays: '',
    isActive: true,
    locked: false,
  };
}

function toDraft(p: Product): ProductDraft {
  return {
    sku: p.sku,
    barcode: p.barcode ?? '',
    name: p.name,
    categoryCode: p.categoryCode ?? '',
    brand: p.brand ?? '',
    baseUom: p.baseUom,
    trackBatch: p.trackBatch,
    trackExpiry: p.trackExpiry,
    shelfLifeDays: p.shelfLifeDays === null ? '' : String(p.shelfLifeDays),
    leadTimeDays: p.leadTimeDays === null ? '' : String(p.leadTimeDays),
    isActive: p.isActive,
    locked: true,
  };
}

/**
 * What actually goes to `POST /products`.
 *
 * A create sends no `sku` and no `barcode` at all — an *absent* key is what tells
 * the server to issue one, and an empty string would be a request to store a
 * blank. On an edit both are present and frozen. `locked` is a property of the
 * form, not of the product, so it never leaves the browser.
 */
function productPayload(draft: ProductDraft): Record<string, unknown> {
  const { locked, sku, barcode, ...rest } = draft;
  return locked ? { ...rest, sku, barcode } : rest;
}

const PRODUCT_FILTERS: Array<{ label: string; test: (p: Product) => boolean }> = [
  { label: 'Active', test: (p) => p.isActive },
  { label: 'In stock', test: (p) => p.isActive && cmpDecimal(p.balanceQty, '0') > 0 },
  { label: 'Out of stock', test: (p) => p.isActive && cmpDecimal(p.balanceQty, '0') <= 0 },
  { label: 'Batch tracked', test: (p) => p.trackBatch },
  { label: 'Deactivated', test: (p) => !p.isActive },
  { label: 'All', test: () => true },
];

/**
 * The catalogue.
 *
 * **A product is a name, a category and a unit.** It used to be twenty fields,
 * and the load-bearing one was `packSizeBase` — a per-product conversion factor
 * that produced Bag and Ton and nothing else, written only when somebody
 * remembered to fill it in. A product created without it had no conversions at
 * all, not even for its own unit, which is what made a purchase entered in any
 * unit refuse to post.
 *
 * The register carries the factors now (`0036`, and Setup → Units of Measure),
 * the database derives every product's conversions from it, and this screen asks
 * for the one unit the item is counted in. Everything a document needs follows.
 */
export function ProductsScreen() {
  const res = useResource<{ products: Product[] }>('/products');
  const refs = useRefs();
  const all = useMemo(() => res.data?.products ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<ProductDraft | null>(null);
  /** The category the last product was filed under — the next one's default. */
  const [lastCategory, setLastCategory] = useState('');
  const action = useAction(res.reload);

  const rows = useMemo(() => {
    const test = PRODUCT_FILTERS[chip]?.test ?? (() => true);
    const needle = search.trim().toLowerCase();
    return all.filter(
      (p) =>
        test(p) &&
        (needle === '' ||
          [p.sku, p.name, p.categoryName ?? ''].some((v) => v.toLowerCase().includes(needle))),
    );
  }, [all, chip, search]);

  const save = () => {
    if (!draft) return;
    const filedUnder = draft.categoryCode;
    void action.run(() =>
      apiSend<{ sku: string; message: string }>(
        'POST',
        '/products',
        productPayload(draft),
        auth(),
      ).then((r) => {
        setLastCategory(filedUnder);
        setDraft(null);
        return r;
      }),
    );
  };

  const remove = (sku: string) => {
    void action.run(() =>
      apiSend<{ message: string }>(
        'DELETE',
        `/products/${encodeURIComponent(sku)}`,
        undefined,
        auth(),
      ),
    );
  };

  const set: RecordSet = {
    crumb: 'Inventory / Product Management',
    title: 'Products',
    subtitle: 'Every product you deal in, with the stock held and what it is worth.',
    chips: PRODUCT_FILTERS.map((f) => f.label),
    cols: [
      { label: 'SKU', width: '150px', mono: true },
      { label: 'Item' },
      { label: 'Category', width: '150px' },
      { label: 'Unit', width: '70px', mono: true },
      // The quantity in the product's own unit, and the unit beside it. It used
      // to be headed "On hand (bags)" and divided by the pack size, which was a
      // count of bags on a book that had never described a bag.
      { label: 'On hand', align: R, mono: true, width: '140px' },
      { label: 'Avg cost', align: R, mono: true, width: '130px' },
      { label: 'Stock value', align: R, mono: true, width: '150px' },
      { label: 'Tracking', align: R, width: '130px' },
    ],
    rows: rows.map((p) => [
      p.sku,
      p.name,
      p.categoryName ?? '—',
      p.baseUom,
      formatQty(p.balanceQty, 0),
      formatRate(p.movingAvgRate, 2),
      formatMoney(p.balanceValue, { decimals: 0, dashOnZero: true }),
      !p.isActive ? 'Deactivated' : p.trackBatch ? 'Batch + expiry' : 'None',
    ]),
    total: [
      '',
      `${rows.length} SKU${rows.length === 1 ? '' : 's'}`,
      '',
      '',
      '',
      '',
      formatMoney(sumDecimal(rows.map((p) => p.balanceValue)), { decimals: 0 }),
      '',
    ],
  };

  const categories = refs.data?.categories ?? [];
  const uoms = refs.data?.uoms ?? [];
  const uomOptions = uoms.map((u) => ({ value: u.code, label: `${u.code} · ${u.name}` }));

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={(i) => {
        const picked = rows[i];
        if (picked) setDraft(toDraft(picked));
      }}
      hideGrid={draft !== null}
      actions={
        <>
          {draft === null && (
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SKU or name"
              aria-label="Search products"
              className="w-[200px]"
            />
          )}
          {/* A catalogue is built in runs: six poultry feeds, then four
              additives. The category of the last product created is a far
              better opening guess than blank, and it is one fewer field to
              visit on five products out of six. */}
          <PanelToggle
            open={draft !== null}
            onToggle={() =>
              setDraft(draft ? null : { ...blankProduct(), categoryCode: lastCategory })
            }
            label="New product"
          />
        </>
      }
    >
      <Messages notice={action.notice} problem={action.problem} />

      <EntryPanel
        title={draft?.locked ? `Edit ${draft.sku}` : 'New product'}
        subtitle="The SKU and the barcode are issued on save. The base unit converts to every other unit of its kind automatically — Setup → Units of Measure holds the factors."
        open={draft !== null}
        onToggle={() => setDraft(null)}
        openLabel="New product"
        actions={
          draft?.locked ? (
            <Button size="sm" onClick={() => remove(draft.sku)} disabled={action.busy}>
              Deactivate
            </Button>
          ) : undefined
        }
      >
        {draft && (
          <>
            <FieldRow>
              <TextField
                label="Item name"
                required
                value={draft.name}
                onChange={(v) => setDraft({ ...draft, name: v })}
              />
              <ComboField
                label="Category"
                caption="Products are filed on a sub-category. The group above it is a heading, not a choice."
                value={draft.categoryCode}
                options={categoryOptions(categories)}
                onChange={(v) => setDraft({ ...draft, categoryCode: v })}
              />
              <TextField
                label="Brand"
                value={draft.brand}
                onChange={(v) => setDraft({ ...draft, brand: v })}
              />
              {/* One unit, and it is the only one. A product can be bought,
                  stocked and sold in any unit of the same kind — the register
                  holds the factors and the database derives the rest — so the
                  three presentation units this form used to ask for were three
                  more questions with no consequence. */}
              <SelectField
                label="Unit"
                required
                hint="What this item is counted in. Every other unit of the same kind converts automatically."
                value={draft.baseUom}
                options={uomOptions}
                onChange={(v) => setDraft({ ...draft, baseUom: v })}
              />
            </FieldRow>

            <FieldRow>
              {/* Named "default" on purpose. The single most common mistake on
                  this form is reading it as *the* expiry: it is not, and the
                  hint says where the real one lives, because that is the
                  question people actually arrive with. */}
              <TextField
                label="Default shelf life (days)"
                hint="Not an expiry date. Each purchase mints its own batch and dates it from this — overwrite it on the line, or correct it later on Batch & Expiry."
                value={draft.shelfLifeDays}
                onChange={(v) => setDraft({ ...draft, shelfLifeDays: v })}
              />
              <TextField
                label="Lead time (days)"
                hint="How long a replenishment takes to arrive. Reporting only — nothing posts from it."
                value={draft.leadTimeDays}
                onChange={(v) => setDraft({ ...draft, leadTimeDays: v })}
              />
              {/* Issued by the server and shown, never typed. Both are identity:
                  the SKU is what the ledger, the stock ledger and every document
                  line point at, and the barcode is printed on a label that is
                  already on a sack by the time anybody would want to edit it. */}
              <ReadOnlyField
                label="SKU"
                value={draft.locked ? draft.sku : null}
                hint={draft.locked ? 'Frozen — history points at it' : undefined}
                placeholder="Issued on save, from the category"
              />
              <ReadOnlyField
                label="Barcode"
                value={draft.locked ? draft.barcode : null}
                hint={draft.locked ? 'EAN-13, printed on the item label' : undefined}
                placeholder="Issued on save"
              />
            </FieldRow>

            <div className="flex flex-wrap items-center gap-5">
              {/* Three, and each one changes what the application does with the
                  product: whether a receipt mints a lot, whether that lot carries
                  a date, and whether the item is offered at all. `Sellable` and
                  `Purchasable` are gone — this book buys and sells the same
                  catalogue, so they were two boxes nobody had a reason to
                  untick, and the server still defaults both to true. */}
              <Checkbox
                label="Batch tracked"
                checked={draft.trackBatch}
                onChange={(e) => setDraft({ ...draft, trackBatch: e.target.checked })}
              />
              <Checkbox
                label="Expiry tracked"
                checked={draft.trackExpiry}
                onChange={(e) => setDraft({ ...draft, trackExpiry: e.target.checked })}
              />
              <Checkbox
                label="Active"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              <Button
                size="sm"
                variant="primary"
                onClick={save}
                disabled={action.busy || draft.name.trim() === '' || draft.baseUom === ''}
              >
                {draft.locked ? 'Save changes' : 'Create product'}
              </Button>
            </div>
          </>
        )}
      </EntryPanel>
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Warehouse inventory
// ═════════════════════════════════════════════════════════════════════════════

interface StockRow {
  warehouseCode: string;
  warehouseName: string;
  sku: string;
  productName: string;
  categoryName: string | null;
  baseUom: string;
  stockUom: string | null;
  packSizeBase: string | null;
  balanceQty: string;
  balanceStockUom: string | null;
  balanceValue: string;
  movingAvgRate: string;
  reservedQty: string;
  availableQty: string;
  reorderLevel: string | null;
  belowReorder: boolean;
  lastMovement: string | null;
  isFrozen: boolean;
}

/**
 * Stock by warehouse.
 *
 * The chips are the warehouses, because that is the question this screen exists
 * to answer — a company-wide total tells a storekeeper nothing about whether the
 * bags are in Karachi or Lahore, and a transfer is the only way to change the
 * answer.
 */
export function WarehouseStockScreen() {
  const refs = useRefs();
  const [warehouse, setWarehouse] = useState<string>('');
  const res = useResource<{ rows: StockRow[] }>('/stock', { warehouse: warehouse || undefined });
  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const warehouses = refs.data?.warehouses ?? [];
  const [search, setSearch] = useState('');

  const chips = ['All warehouses', ...warehouses.map((w) => w.name)];
  const chipIndex = warehouse === '' ? 0 : warehouses.findIndex((w) => w.code === warehouse) + 1;

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter(
      (r) => needle === '' || [r.sku, r.productName].some((v) => v.toLowerCase().includes(needle)),
    );
  }, [all, search]);

  const belowReorder = rows.filter((r) => r.belowReorder).length;
  const frozen = rows.filter((r) => r.isFrozen).length;

  const set: RecordSet = {
    crumb: 'Inventory / Warehouse Inventory',
    title: 'Warehouse stock',
    subtitle: 'How much of each item is held at each warehouse.',
    chips,
    cols: [
      { label: 'SKU', width: '140px', mono: true },
      { label: 'Item' },
      { label: 'Warehouse', width: '170px' },
      { label: 'On hand', align: R, mono: true, width: '120px' },
      { label: 'Reserved', align: R, mono: true, width: '110px' },
      { label: 'Avg / Kg', align: R, mono: true, width: '110px' },
      { label: 'Value', align: R, mono: true, width: '150px' },
      { label: 'State', align: R, width: '130px', badge: true },
    ],
    rows: rows.map((r) => [
      r.sku,
      r.productName,
      r.warehouseName,
      `${formatQty(r.balanceStockUom ?? r.balanceQty, 0)} ${r.stockUom ?? r.baseUom}`,
      cmpDecimal(r.reservedQty, '0') > 0 ? formatQty(r.reservedQty, 0) : '—',
      formatRate(r.movingAvgRate, 2),
      formatMoney(r.balanceValue, { decimals: 0 }),
      r.isFrozen ? 'Frozen' : r.belowReorder ? 'Below Reorder' : 'OK',
    ]),
    total: [
      '',
      `${rows.length} line${rows.length === 1 ? '' : 's'}`,
      '',
      '',
      '',
      '',
      formatMoney(sumDecimal(rows.map((r) => r.balanceValue)), { decimals: 0 }),
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chipIndex < 0 ? 0 : chipIndex}
      onChipChange={(i) => setWarehouse(i === 0 ? '' : (warehouses[i - 1]?.code ?? ''))}
      actions={
        <>
          <Input
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SKU or item"
            aria-label="Search stock"
            className="w-[200px]"
          />
        </>
      }
    >
      <Tiles>
        <Card>
          <StatTile
            label="Stock value"
            value={formatMoney(sumDecimal(rows.map((r) => r.balanceValue)), { decimals: 0 })}
            hint="weighted average"
          />
        </Card>
        <Card>
          <StatTile label="Lines shown" value={String(rows.length)} hint={`${all.length} in all`} />
        </Card>
        <Card>
          <StatTile
            label="Below reorder"
            value={String(belowReorder)}
            hint="per SKU per warehouse"
          />
        </Card>
        <Card>
          <StatTile label="Frozen by count" value={String(frozen)} hint="no movement allowed" />
        </Card>
      </Tiles>
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Stock ledger
// ═════════════════════════════════════════════════════════════════════════════

interface LedgerRow {
  date: string;
  seq: string;
  docType: string;
  docNo: string | null;
  voucherType: string | null;
  voucherNo: string | null;
  sku: string;
  productName: string;
  warehouseCode: string;
  warehouseName: string;
  batchNo: string | null;
  direction: 'IN' | 'OUT';
  qtyIn: string;
  qtyOut: string;
  enteredQty: string | null;
  enteredUom: string | null;
  unitRate: string;
  valueIn: string;
  valueOut: string;
  balanceQty: string;
  balanceValue: string;
  movingAvgRate: string;
  partyName: string | null;
  narration: string | null;
}

/**
 * The immutable movement history.
 *
 * There is no edit here and there never will be: a correction is an offsetting
 * movement, and `stock_ledger_immutable_bud` refuses an UPDATE or a DELETE
 * outright. What the screen offers instead is the running balance and the
 * moving average as at each row, which is the only way to see what a sale on a
 * particular Tuesday actually cost.
 */
export function StockLedgerScreen() {
  const refs = useRefs();
  const [sku, setSku] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const res = useResource<{ rows: LedgerRow[] }>('/stock-ledger', {
    sku: sku || undefined,
    warehouse: warehouse || undefined,
    from: from || undefined,
    to: to || undefined,
  });
  const products = useResource<{ products: Product[] }>('/products');
  const [chip, setChip] = useState(0);

  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const filters: Array<{ label: string; test: (r: LedgerRow) => boolean }> = [
    { label: 'All movements', test: () => true },
    { label: 'Receipts', test: (r) => r.direction === 'IN' },
    { label: 'Issues', test: (r) => r.direction === 'OUT' },
    { label: 'Transfers', test: (r) => r.docType.startsWith('STR') },
    { label: 'Adjustments', test: (r) => r.docType === 'ADJ' || r.docType === 'COUNT' },
  ];
  const rows = useMemo(
    () => all.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, chip],
  );

  const set: RecordSet = {
    crumb: 'Inventory / Stock Ledger',
    title: 'Stock ledger',
    subtitle: 'Every stock movement in and out, in the order it happened.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Date', width: '104px', mono: true },
      { label: 'Document', width: '150px', mono: true },
      { label: 'SKU', width: '130px', mono: true },
      { label: 'Warehouse', width: '150px' },
      { label: 'Batch', width: '150px', mono: true },
      { label: 'In', align: R, mono: true, width: '110px' },
      { label: 'Out', align: R, mono: true, width: '110px' },
      { label: 'Rate', align: R, mono: true, width: '110px' },
      { label: 'Balance', align: R, mono: true, width: '120px' },
      { label: 'Value', align: R, mono: true, width: '140px' },
    ],
    rows: rows.map((r) => [
      formatDate(r.date),
      `${r.docType} ${r.docNo ?? ''}`.trim(),
      r.sku,
      r.warehouseName,
      r.batchNo ?? '—',
      cmpDecimal(r.qtyIn, '0') > 0 ? formatQty(r.qtyIn, 0) : '—',
      cmpDecimal(r.qtyOut, '0') > 0 ? formatQty(r.qtyOut, 0) : '—',
      formatRate(r.unitRate, 2),
      formatQty(r.balanceQty, 0),
      formatMoney(r.balanceValue, { decimals: 0 }),
    ]),
    total: [
      '',
      `${rows.length} movement${rows.length === 1 ? '' : 's'}`,
      '',
      '',
      '',
      formatQty(sumDecimal(rows.map((r) => r.qtyIn)), 0),
      formatQty(sumDecimal(rows.map((r) => r.qtyOut)), 0),
      '',
      '',
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      emptyTitle="No movement matches these filters"
      actions={
        <>
          {/* These two were raw `<select>`s outside the design system, listing
              every SKU in the catalogue by code alone — the one filter on this
              screen somebody actually reaches for, and the slowest way to use
              it. Type-to-find matches the item name too. */}
          <div className="w-[190px]">
            <Combobox
              variant="field"
              aria-label="Filter by SKU"
              placeholder="Every SKU"
              value={sku}
              options={[
                { value: '', label: 'Every SKU' },
                ...(products.data?.products ?? []).map((p) => ({
                  value: p.sku,
                  label: p.sku,
                  hint: p.name,
                  keywords: p.name,
                })),
              ]}
              onChange={setSku}
            />
          </div>
          <div className="w-[190px]">
            <Combobox
              variant="field"
              aria-label="Filter by warehouse"
              placeholder="Every warehouse"
              value={warehouse}
              options={[
                { value: '', label: 'Every warehouse' },
                ...(refs.data?.warehouses ?? []).map((w) => ({
                  value: w.code,
                  label: w.name,
                  hint: w.code,
                })),
              ]}
              onChange={setWarehouse}
            />
          </div>
          <Input
            size="sm"
            type="date"
            value={from}
            aria-label="From date"
            onChange={(e) => setFrom(e.target.value)}
            className="w-[140px]"
          />
          <Input
            size="sm"
            type="date"
            value={to}
            aria-label="To date"
            onChange={(e) => setTo(e.target.value)}
            className="w-[140px]"
          />
        </>
      }
    />
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Stock transfer
// ═════════════════════════════════════════════════════════════════════════════

interface TransferRow {
  transferNo: string;
  transferDate: string;
  fromCode: string;
  fromName: string;
  toCode: string;
  toName: string;
  status: string;
  transporter: string | null;
  vehicleNo: string | null;
  narration: string | null;
  dispatchedAt: string | null;
  receivedAt: string | null;
  dispatchVoucherNo: string | null;
  receiptVoucherNo: string | null;
  lineCount: number;
  qtyBase: string;
  valueBase: string;
}

interface TransferLineDraft {
  sku: string;
  enteredQty: string;
  uomCode: string;
  batchNo: string;
}

/**
 * Warehouse A → Stock in Transit → warehouse B, in two confirmations.
 *
 * The in-transit account is not decoration. Between the vehicle leaving and the
 * vehicle arriving the stock is real, valued and in neither warehouse, and the
 * only honest place for it is its own asset account. A short receipt clears the
 * remainder to Stock Adjustment rather than leaving it in transit for ever.
 */
export function StockTransferScreen() {
  const res = useResource<{ transfers: TransferRow[] }>('/transfers');
  const refs = useRefs();
  const products = useResource<{ products: Product[] }>('/products');
  const all = useMemo(() => res.data?.transfers ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const action = useAction(res.reload);

  const warehouses = refs.data?.warehouses ?? [];
  // `transporter` was in this state with no control rendering it and nothing
  // reading it back — a field that existed only in a type. Dropped rather than
  // given a box, because a transfer between two of the company's own warehouses
  // is not usually a haulier's job and the vehicle number already covers it.
  const [header, setHeader] = useState({
    transferDate: today(),
    fromWarehouse: '',
    toWarehouse: '',
    vehicleNo: '',
    narration: '',
  });
  const blankTransferLine = (): TransferLineDraft => ({
    sku: '',
    enteredQty: '',
    // Setup → Company Profile rather than the literal `'BAG'` that was here.
    // A chosen item's own stock unit still wins — see `pickedLine`.
    uomCode: defaultUom(),
    batchNo: '',
  });
  const [lines, setLines] = useState<TransferLineDraft[]>([blankTransferLine()]);

  useEffect(() => {
    if (header.fromWarehouse === '' && warehouses.length > 0) {
      setHeader((h) => ({
        ...h,
        fromWarehouse: warehouses.find((w) => w.isDefault)?.code ?? warehouses[0]?.code ?? '',
        toWarehouse: warehouses.find((w) => !w.isDefault)?.code ?? '',
      }));
    }
  }, [warehouses, header.fromWarehouse]);

  const filters: Array<{ label: string; test: (t: TransferRow) => boolean }> = [
    { label: 'All', test: () => true },
    { label: 'Draft', test: (t) => t.status === 'DRAFT' },
    { label: 'In transit', test: (t) => t.status === 'DISPATCHED' },
    { label: 'Received', test: (t) => t.status === 'RECEIVED' },
  ];
  const rows = useMemo(
    () => all.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, chip],
  );
  const active = selected === null ? null : (rows[selected] ?? null);

  useAutoRows(lines, setLines, blankTransferLine, (l) => l.sku === '' && l.enteredQty === '');
  useDefaultUnits(lines, setLines);
  const postable =
    !action.busy &&
    header.fromWarehouse !== '' &&
    header.toWarehouse !== '' &&
    lines.some((l) => l.sku !== '' && l.enteredQty !== '');

  const save = () => {
    void action.run(() =>
      apiSend<{ transferNo: string; message: string }>(
        'POST',
        '/transfers',
        {
          ...header,
          lines: lines
            .filter((l) => l.sku !== '' && l.enteredQty !== '')
            .map((l) => ({ ...l, batchNo: l.batchNo || undefined })),
        },
        auth(),
      ).then((r) => {
        setOpen(false);
        setLines([blankTransferLine()]);
        return r;
      }),
    );
  };

  useSubmitShortcut(open && postable, save);

  const advance = (transferNo: string, verb: 'dispatch' | 'receive') => {
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/transfers/${transferNo}/${verb}`, {}, auth()),
    );
  };

  const set: RecordSet = {
    crumb: 'Inventory / Stock Transfer',
    title: 'Stock transfers',
    subtitle: 'Move stock from one warehouse to another.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Transfer', width: '150px', mono: true },
      { label: 'Date', width: '104px', mono: true },
      { label: 'From' },
      { label: 'To' },
      { label: 'Lines', align: R, mono: true, width: '80px' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Value', align: R, mono: true, width: '140px' },
      { label: 'Status', align: R, width: '130px', badge: true },
    ],
    rows: rows.map((t) => [
      t.transferNo,
      formatDate(t.transferDate),
      t.fromName,
      t.toName,
      String(t.lineCount),
      formatQty(t.qtyBase, 0),
      formatMoney(t.valueBase, { decimals: 0, dashOnZero: true }),
      humanizeStatus(t.status),
    ]),
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      selectedRow={selected}
      onRowSelect={(i) => setSelected(selected === i ? null : i)}
      emptyTitle="No transfer yet"
      hideGrid={open}
      actions={<PanelToggle open={open} onToggle={() => setOpen(!open)} label="New transfer" />}
    >
      <Messages notice={action.notice} problem={action.problem} />

      {active && !open && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={`${active.transferNo} · ${active.fromName} → ${active.toName}`}
            subtitle={
              active.status === 'DRAFT'
                ? 'Nothing has moved yet'
                : active.status === 'DISPATCHED'
                  ? `Dispatched ${formatDate(active.dispatchedAt)} · ${active.dispatchVoucherNo} holds the value in transit`
                  : `Received ${formatDate(active.receivedAt)} · ${active.receiptVoucherNo}`
            }
            actions={
              active.status === 'DRAFT' ? (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={action.busy}
                  onClick={() => advance(active.transferNo, 'dispatch')}
                >
                  Dispatch
                </Button>
              ) : active.status === 'DISPATCHED' ? (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={action.busy}
                  onClick={() => advance(active.transferNo, 'receive')}
                >
                  Confirm receipt
                </Button>
              ) : (
                <Badge tone="ok">Closed</Badge>
              )
            }
          />
        </Card>
      )}

      <EntryPanel
        title="New stock transfer"
        subtitle="Saved as a draft — nothing leaves the source warehouse until it is dispatched"
        open={open}
        onToggle={() => setOpen(false)}
        openLabel="New transfer"
        actions={
          <>
            <SubmitHint />
            <Button size="sm" variant="primary" disabled={!postable} onClick={save}>
              Save draft
            </Button>
          </>
        }
      >
        <FieldRow>
          <TextField
            label="Transfer date"
            type="date"
            required
            value={header.transferDate}
            onChange={(v) => setHeader({ ...header, transferDate: v })}
          />
          <ComboField
            label="From warehouse"
            required
            value={header.fromWarehouse}
            options={warehouses.map((w) => ({ value: w.code, label: w.name, hint: w.code }))}
            onChange={(v) => setHeader({ ...header, fromWarehouse: v })}
          />
          <ComboField
            label="To warehouse"
            required
            value={header.toWarehouse}
            options={warehouses
              .filter((w) => w.code !== header.fromWarehouse)
              .map((w) => ({ value: w.code, label: w.name, hint: w.code }))}
            onChange={(v) => setHeader({ ...header, toWarehouse: v })}
          />
          <TextField
            label="Vehicle"
            value={header.vehicleNo}
            placeholder="TLC-4471"
            onChange={(v) => setHeader({ ...header, vehicleNo: v })}
          />
        </FieldRow>

        <EntryGrid
          aria-label="Transfer lines"
          columns={[
            { key: 'no', label: '#', width: '44px', noPrint: true },
            { key: 'sku', label: 'SKU', width: '220px', required: true },
            { key: 'batch', label: 'Batch', width: '180px' },
            { key: 'qty', label: 'Qty', width: '130px', numeric: true, required: true },
            { key: 'uom', label: 'Unit', width: '110px' },
            { key: 'del', label: '', width: '44px', noPrint: true },
          ]}
        >
          {lines.map((line, i) => (
            <EntryRow key={i}>
              <EntryCell padded className="text-[var(--ink-3)]">
                {i + 1}
              </EntryCell>
              <EntryCell>
                <Combobox
                  value={line.sku}
                  aria-label={`Line ${i + 1} SKU`}
                  placeholder="Type a SKU or item name…"
                  options={(products.data?.products ?? [])
                    .filter((p) => p.isActive)
                    .map((p) => ({
                      value: p.sku,
                      label: `${p.sku} · ${p.name}`,
                      keywords: `${p.brand ?? ''} ${p.categoryName ?? ''}`,
                    }))}
                  onChange={(sku) => {
                    const picked = pickedLine(
                      sku,
                      products.data?.products,
                      'STOCK',
                      line.enteredQty,
                    );
                    const next = [...lines];
                    next[i] = { ...line, sku, enteredQty: picked.qty, uomCode: picked.uom };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridInput
                  value={line.batchNo}
                  placeholder="FEFO if blank"
                  aria-label={`Line ${i + 1} batch`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, batchNo: e.target.value };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridInput
                  numeric
                  value={line.enteredQty}
                  aria-label={`Line ${i + 1} quantity`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, enteredQty: e.target.value };
                    setLines(next);
                  }}
                />
              </EntryCell>
              <EntryCell>
                <GridSelect
                  value={line.uomCode}
                  aria-label={`Line ${i + 1} unit`}
                  onChange={(e) => {
                    const next = [...lines];
                    next[i] = { ...line, uomCode: e.target.value };
                    setLines(next);
                  }}
                >
                  {(refs.data?.uoms ?? []).map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.code}
                    </option>
                  ))}
                </GridSelect>
              </EntryCell>
              <EntryCell noPrint>
                <GridAction
                  label={`Remove line ${i + 1}`}
                  disabled={lines.length === 1}
                  onClick={() => setLines(lines.filter((_, j) => j !== i))}
                >
                  ×
                </GridAction>
              </EntryCell>
            </EntryRow>
          ))}
        </EntryGrid>

        {/* Narration on its own, now that Save draft has moved to the header. */}
        <div className="flex flex-col">
          <label
            htmlFor="transfer-narration"
            className="bb-eyebrow pb-1.5 text-[10px] text-[var(--ink-3)]"
          >
            Narration
          </label>
          <Textarea
            id="transfer-narration"
            rows={2}
            value={header.narration}
            placeholder="Reason for the move, gate pass, who is carrying it"
            aria-label="Narration"
            onChange={(e) => setHeader({ ...header, narration: e.target.value })}
          />
        </div>
      </EntryPanel>
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Physical count
// ═════════════════════════════════════════════════════════════════════════════

interface CountRow {
  sessionNo: string;
  warehouseCode: string;
  warehouseName: string;
  scopeKind: string;
  scopeCategory: string | null;
  status: string;
  blindCount: boolean;
  frozenAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  approvedAt: string | null;
  adjustmentNo: string | null;
  voucherNo: string | null;
  narration: string | null;
  lineCount: number;
  countedLines: number;
  varianceLines: number;
  shortageQty: string;
  excessQty: string;
  netVarianceValue: string;
}

interface CountLine {
  id: string;
  sku: string;
  productName: string;
  batchNo: string | null;
  binCode: string | null;
  systemQty: string;
  countedQty: string | null;
  varianceQty: string;
  unitRate: string;
  varianceValue: string;
  mode: string;
  scanCount: number;
  reasonCode: string | null;
  reasonName: string | null;
  countedBy: string | null;
  countedAt: string | null;
  note: string | null;
}

/**
 * Count session, count sheet, variance, posting.
 *
 * Two things here are the whole control and both are easy to skip. The session
 * **freezes** the SKUs it covers — `trg_stock_count_freeze_guard` refuses any
 * other movement on them until it closes — because counting a warehouse that is
 * still dispatching produces a variance report about the counting. And a **blind
 * count** hides the system quantity from the counter, because a counter who can
 * see the expected figure will find it.
 *
 * An uncounted line posts as agreeing with the system, not as zero. The opposite
 * reading would write off a warehouse because somebody stopped for lunch.
 */
export function PhysicalCountScreen() {
  const res = useResource<{ sessions: CountRow[] }>('/counts');
  const refs = useRefs();
  const all = useMemo(() => res.data?.sessions ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [warehouse, setWarehouse] = useState('');
  const [blind, setBlind] = useState(true);
  const action = useAction(res.reload);

  const detail = useResource<{ header: Record<string, unknown>; lines: CountLine[] }>(
    openSession ? '/count' : null,
    { no: openSession ?? undefined },
  );
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  /** Narrows what the sheet shows. Never what it posts — see `shownLines`. */
  const [sheetFilter, setSheetFilter] = useState('');

  useEffect(() => {
    setCounted({});
    setReasons({});
    setSheetFilter('');
  }, [openSession]);

  const warehouses = refs.data?.warehouses ?? [];
  useEffect(() => {
    if (warehouse === '' && warehouses.length > 0) {
      setWarehouse(warehouses.find((w) => w.isDefault)?.code ?? warehouses[0]?.code ?? '');
    }
  }, [warehouses, warehouse]);

  const filters: Array<{ label: string; test: (c: CountRow) => boolean }> = [
    { label: 'Open', test: (c) => !['POSTED', 'CANCELLED'].includes(c.status) },
    { label: 'Posted', test: (c) => c.status === 'POSTED' },
    { label: 'Cancelled', test: (c) => c.status === 'CANCELLED' },
    { label: 'All', test: () => true },
  ];
  const rows = useMemo(
    () => all.filter(filters[chip]?.test ?? (() => true)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, chip],
  );

  const reload = () => {
    res.reload();
    detail.reload();
  };

  const create = () => {
    void action.run(() =>
      apiSend<{ sessionNo: string; message: string }>(
        'POST',
        '/counts',
        { warehouse, scopeKind: 'FULL', blindCount: blind },
        auth(),
      ).then((r) => {
        setCreating(false);
        setOpenSession(r.sessionNo);
        return r;
      }),
    );
  };

  const countedLines = () =>
    Object.entries(counted)
      .filter(([, v]) => v.trim() !== '')
      .map(([id, v]) => ({
        id,
        countedQty: v.trim(),
        mode: 'MANUAL',
        reasonCode: reasons[id] || undefined,
      }));

  const record = () => {
    if (!openSession) return;
    const payload = countedLines();
    if (payload.length === 0) return;
    void action.run(() =>
      apiSend<{ message: string }>(
        'POST',
        `/counts/${openSession}/lines`,
        { lines: payload },
        auth(),
      ).then((r) => {
        reload();
        return r;
      }),
    );
  };

  const post = () => {
    if (!openSession) return;
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/counts/${openSession}/post`, {}, auth()).then((r) => {
        reload();
        return r;
      }),
    );
  };

  /**
   * One button for the ordinary case: the sheet is finished and the variance is
   * to be posted.
   *
   * Recording and posting were always two clicks and a round trip apart even
   * though the counter, standing in the warehouse having just written the last
   * figure, wants both. They stay separate buttons as well, because they are
   * genuinely separate acts — a count interrupted halfway is recorded and not
   * posted, and a supervisor may want to look at the variance before it hits
   * Stock Adjustment. This does the two in order and stops if the first is
   * refused, which is the only ordering that can be right.
   */
  const recordAndPost = () => {
    if (!openSession) return;
    const payload = countedLines();
    void action.run(async () => {
      if (payload.length > 0) {
        await apiSend<{ message: string }>(
          'POST',
          `/counts/${openSession}/lines`,
          { lines: payload },
          auth(),
        );
      }
      const posted = await apiSend<{ message: string }>(
        'POST',
        `/counts/${openSession}/post`,
        {},
        auth(),
      );
      reload();
      return posted;
    });
  };

  const cancel = () => {
    if (!openSession) return;
    void action.run(() =>
      apiSend<{ message: string }>('POST', `/counts/${openSession}/cancel`, {}, auth()).then(
        (r) => {
          setOpenSession(null);
          reload();
          return r;
        },
      ),
    );
  };

  const set: RecordSet = {
    crumb: 'Inventory / Physical Count',
    title: 'Physical stock count',
    subtitle: 'Count the stock in a warehouse and post any difference.',
    chips: filters.map((f) => f.label),
    cols: [
      { label: 'Session', width: '150px', mono: true },
      { label: 'Warehouse' },
      { label: 'Scope', width: '110px' },
      { label: 'Lines', align: R, mono: true, width: '90px' },
      { label: 'Counted', align: R, mono: true, width: '100px' },
      { label: 'Variances', align: R, mono: true, width: '110px' },
      { label: 'Net variance', align: R, mono: true, width: '150px' },
      { label: 'Status', align: R, width: '130px', badge: true },
    ],
    rows: rows.map((c) => [
      c.sessionNo,
      c.warehouseName,
      c.scopeCategory ?? humanizeStatus(c.scopeKind),
      String(c.lineCount),
      String(c.countedLines),
      String(c.varianceLines),
      formatMoney(c.netVarianceValue, { decimals: 0, dashOnZero: true }),
      humanizeStatus(c.status),
    ]),
  };

  const lines = detail.data?.lines ?? [];
  /**
   * What the sheet is showing, which is not always what it holds.
   *
   * `lines` stays the whole frozen snapshot — the counted/total figure in the
   * subtitle, and everything `record` and `post` send, read it — so filtering
   * can never quietly narrow what gets posted. This is the display list alone.
   */
  const shownLines = (() => {
    const q = sheetFilter.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((l) =>
      [l.sku, l.productName, l.batchNo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  })();
  const sheetStatus = (detail.data?.header as { status?: string } | undefined)?.status;
  const isBlind = (detail.data?.header as { blindCount?: boolean } | undefined)?.blindCount ?? true;
  const editable = sheetStatus !== undefined && !['POSTED', 'CANCELLED'].includes(sheetStatus);

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={(i) => setOpenSession(rows[i]?.sessionNo ?? null)}
      emptyTitle="No count session yet"
      // The count sheet takes the screen too. It is not a preview of a row —
      // it is the document being worked on, forty SKUs deep, and the register
      // of past sessions underneath it is nothing anybody counting needs.
      hideGrid={creating || openSession !== null}
      actions={
        <PanelToggle
          open={creating}
          onToggle={() => setCreating(!creating)}
          label="Freeze & count"
        />
      }
    >
      <Messages notice={action.notice} problem={action.problem} />

      <EntryPanel
        title="New count session"
        subtitle="Freezing takes a snapshot at this instant and stops every other movement on the SKUs it covers"
        open={creating}
        onToggle={() => setCreating(false)}
        openLabel="Freeze & count"
      >
        <FieldRow>
          <ComboField
            label="Warehouse"
            required
            value={warehouse}
            options={warehouses.map((w) => ({ value: w.code, label: w.name, hint: w.code }))}
            onChange={setWarehouse}
          />
          <div className="flex items-end pb-1">
            <Checkbox
              label="Blind count — hide the system quantity"
              checked={blind}
              onChange={(e) => setBlind(e.target.checked)}
            />
          </div>
          <div className="flex items-end pb-1">
            <Button
              size="sm"
              variant="primary"
              disabled={action.busy || !warehouse}
              onClick={create}
            >
              Freeze and open the sheet
            </Button>
          </div>
        </FieldRow>
      </EntryPanel>

      {openSession && !creating && (
        <Card flush className="mb-3.5">
          <CardHeader
            title={`Count sheet ${openSession}`}
            subtitle={
              editable
                ? `${lines.filter((l) => l.countedQty !== null).length} of ${lines.length} counted${isBlind ? ' · blind' : ''}`
                : `${sheetStatus === 'POSTED' ? 'Posted' : 'Closed'} — the sheet is read-only`
            }
            actions={
              <>
                {/*
                  A frozen warehouse is four hundred rows and the person holding
                  the sheet is standing in front of one pallet. Filtering by SKU,
                  item or batch is the difference between counting a warehouse
                  and scrolling one. It filters what is *shown* and nothing else:
                  a quantity typed into a row that is later filtered out is still
                  in `counted` and still posts.
                */}
                <Input
                  size="sm"
                  value={sheetFilter}
                  onChange={(e) => setSheetFilter(e.target.value)}
                  placeholder="SKU, item or batch"
                  aria-label="Filter count sheet"
                  className="w-[200px]"
                />
                <Button size="sm" onClick={() => setOpenSession(null)}>
                  Close
                </Button>
                {editable && (
                  <>
                    <Button size="sm" onClick={cancel} disabled={action.busy}>
                      Cancel session
                    </Button>
                    {/* The two halves stay reachable — a count broken off at
                        lunchtime is recorded and not posted — but the ordinary
                        end of a count is one button, not two. */}
                    <Button size="sm" onClick={record} disabled={action.busy}>
                      Record only
                    </Button>
                    <Button size="sm" onClick={post} disabled={action.busy}>
                      Post variance
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={recordAndPost}
                      disabled={action.busy}
                    >
                      Record &amp; post variance
                    </Button>
                  </>
                )}
              </>
            }
          />
          <EntryGrid
            aria-label="Count lines"
            columns={[
              { key: 'sku', label: 'SKU', width: '160px' },
              { key: 'item', label: 'Item' },
              { key: 'batch', label: 'Batch', width: '160px' },
              ...(isBlind && editable
                ? []
                : [{ key: 'sys', label: 'System', width: '120px', numeric: true }]),
              { key: 'count', label: 'Counted', width: '130px', numeric: true },
              { key: 'var', label: 'Variance', width: '120px', numeric: true },
              { key: 'val', label: 'Value', width: '140px', numeric: true },
              { key: 'reason', label: 'Reason', width: '190px' },
            ]}
          >
            {shownLines.map((l) => {
              const entered = counted[l.id] ?? l.countedQty ?? '';
              const variance =
                entered === '' ? null : (Number(entered) - Number(l.systemQty)).toFixed(3);
              return (
                <EntryRow
                  key={l.id}
                  tone={variance && Number(variance) !== 0 ? 'invalid' : 'default'}
                >
                  <EntryCell padded className="bb-num text-[var(--accent)]">
                    {l.sku}
                  </EntryCell>
                  <EntryCell padded>{l.productName}</EntryCell>
                  <EntryCell padded className="bb-num text-[var(--ink-2)]">
                    {l.batchNo ?? '—'}
                  </EntryCell>
                  {!(isBlind && editable) && (
                    <EntryCell padded numeric>
                      {formatQty(l.systemQty, 0)}
                    </EntryCell>
                  )}
                  <EntryCell>
                    <GridInput
                      numeric
                      disabled={!editable}
                      value={entered}
                      aria-label={`Counted quantity for ${l.sku}`}
                      onChange={(e) => setCounted({ ...counted, [l.id]: e.target.value })}
                    />
                  </EntryCell>
                  <EntryCell padded numeric>
                    {variance === null ? '—' : formatQty(variance, 0)}
                  </EntryCell>
                  <EntryCell padded numeric>
                    {variance === null
                      ? '—'
                      : formatMoney((Number(variance) * Number(l.unitRate)).toFixed(4), {
                          decimals: 0,
                        })}
                  </EntryCell>
                  <EntryCell>
                    <GridSelect
                      disabled={!editable}
                      value={reasons[l.id] ?? l.reasonCode ?? ''}
                      aria-label={`Variance reason for ${l.sku}`}
                      onChange={(e) => setReasons({ ...reasons, [l.id]: e.target.value })}
                    >
                      <option value="">—</option>
                      {(refs.data?.reasonCodes ?? [])
                        .filter((r) => r.domain === 'COUNT_VARIANCE')
                        .map((r) => (
                          <option key={r.code} value={r.code}>
                            {r.name}
                          </option>
                        ))}
                    </GridSelect>
                  </EntryCell>
                </EntryRow>
              );
            })}
          </EntryGrid>
          {lines.length === 0 && (
            <CardBody className="text-[12.5px] text-[var(--ink-3)]">
              This session covers no stock — the warehouse held nothing when it was frozen.
            </CardBody>
          )}
        </Card>
      )}
    </RecordGrid>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Batch & expiry
// ═════════════════════════════════════════════════════════════════════════════

interface BatchRow {
  sku: string;
  productName: string;
  warehouseName: string;
  batchNo: string;
  manufactureDate: string | null;
  expiryDate: string | null;
  receivedDate: string | null;
  originCountry: string | null;
  supplierBatchNo: string | null;
  supplierName: string | null;
  qualityNote: string | null;
  isQuarantined: boolean;
  landedUnitCost: string | null;
  balanceQty: string;
  balanceValue: string;
  daysToExpiry: number | null;
  expiryStatus: string;
}

const BATCH_FILTERS: Array<{ label: string; test: (b: BatchRow) => boolean }> = [
  { label: 'All batches', test: () => true },
  { label: 'Expired', test: (b) => b.expiryStatus === 'Expired' },
  { label: 'Within 30 days', test: (b) => b.expiryStatus === 'Expiring 30 days' },
  { label: 'Within 90 days', test: (b) => b.expiryStatus === 'Expiring 90 days' },
  { label: 'Quarantined', test: (b) => b.isQuarantined },
  { label: 'No expiry', test: (b) => b.expiryStatus === 'No expiry' },
];

/**
 * The expiry dashboard, and the one place a lot's dates can be corrected.
 *
 * Value at risk is the figure that matters: a batch three weeks from expiry is
 * not a quantity problem, it is money about to leave the balance sheet, and the
 * only lever is to sell it first — which is what FEFO does automatically on
 * every invoice line that does not name its own batch.
 *
 * **Where the expiry of a repeat purchase comes from.** Nothing here invents a
 * date. Every receipt of a batch-tracked product mints a batch, and the batch
 * takes its expiry from the purchase line; the line takes its suggestion from
 * the product's *default shelf life* and lets the operator overwrite it with
 * what the sack says. Two consignments of one SKU are therefore two rows here
 * with two dates, which is the whole point — a single expiry on the product
 * master would be a claim about stock that has not arrived yet. This screen is
 * where the claim is corrected when the paperwork turns out to be wrong.
 */
export function BatchExpiryScreen() {
  const res = useResource<{ rows: BatchRow[] }>('/batches');
  const all = useMemo(() => res.data?.rows ?? [], [res.data]);
  const [chip, setChip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const action = useAction(res.reload);

  const rows = useMemo(() => all.filter(BATCH_FILTERS[chip]?.test ?? (() => true)), [all, chip]);
  const atRisk = all.filter(
    (b) => b.expiryStatus === 'Expired' || b.expiryStatus === 'Expiring 30 days',
  );
  const key = (b: BatchRow) => `${b.sku}|${b.batchNo}`;
  const current = selected ? (all.find((b) => key(b) === selected) ?? null) : null;

  const set: RecordSet = {
    crumb: 'Inventory / Batch & Expiry',
    title: 'Batch & expiry monitor',
    subtitle: 'Every batch you hold, with its manufacture and expiry dates.',
    chips: BATCH_FILTERS.map((f) => f.label),
    cols: [
      { label: 'Batch', width: '190px', mono: true },
      { label: 'SKU', width: '140px', mono: true },
      { label: 'Item' },
      { label: 'Made', width: '110px', mono: true },
      { label: 'Expiry', width: '110px', mono: true },
      { label: 'Days', align: R, mono: true, width: '80px' },
      { label: 'Warehouse', width: '150px' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '110px' },
      { label: 'Value', align: R, mono: true, width: '130px' },
      { label: 'Status', align: R, width: '150px', badge: true },
    ],
    rows: rows.map((b) => [
      b.batchNo,
      b.sku,
      b.productName,
      b.manufactureDate ? formatDate(b.manufactureDate) : '—',
      b.expiryDate ? formatDate(b.expiryDate) : '—',
      b.daysToExpiry === null ? '—' : String(b.daysToExpiry),
      b.warehouseName,
      formatQty(b.balanceQty, 0),
      formatMoney(b.balanceValue, { decimals: 0 }),
      b.isQuarantined ? 'Quarantined' : b.expiryStatus,
    ]),
    total: [
      '',
      '',
      `${rows.length} batch${rows.length === 1 ? '' : 'es'}`,
      '',
      '',
      '',
      '',
      formatQty(sumDecimal(rows.map((b) => b.balanceQty)), 0),
      formatMoney(sumDecimal(rows.map((b) => b.balanceValue)), { decimals: 0 }),
      '',
    ],
  };

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={setChip}
      onRowSelect={(i) => {
        const row = rows[i];
        setSelected(row && key(row) !== selected ? key(row) : null);
      }}
      emptyTitle="No batch in this view"
    >
      <Messages notice={action.notice} problem={action.problem} />

      {current && (
        <BatchEditor
          key={selected ?? ''}
          batch={current}
          busy={action.busy}
          onClose={() => setSelected(null)}
          onSave={(patch) =>
            void action.run(() =>
              apiSend<{ message: string }>(
                'PATCH',
                `/batches/${encodeURIComponent(current.sku)}/${encodeURIComponent(current.batchNo)}`,
                patch,
                auth(),
              ),
            )
          }
        />
      )}

      <Tiles>
        <Card>
          <StatTile label="Batches held" value={String(all.length)} hint="with a live balance" />
        </Card>
        <Card>
          <StatTile
            label="Value at risk"
            value={formatMoney(sumDecimal(atRisk.map((b) => b.balanceValue)), { decimals: 0 })}
            hint="expired or inside 30 days"
          />
        </Card>
        <Card>
          <StatTile
            label="Expired"
            value={String(all.filter((b) => b.expiryStatus === 'Expired').length)}
            hint="past the date, still on hand"
          />
        </Card>
        <Card>
          <StatTile
            label="Total stock value"
            value={formatMoney(sumDecimal(all.map((b) => b.balanceValue)), { decimals: 0 })}
            hint="batch-tracked only"
          />
        </Card>
      </Tiles>
    </RecordGrid>
  );
}

/**
 * Correct a lot's dates, or take it out of circulation.
 *
 * The batch number and the product are not editable and are shown as facts
 * rather than as disabled inputs: they are the lot's identity, stock ledger rows
 * point at it, and a greyed-out box invites somebody to hunt for the permission
 * that would unlock it. There is none.
 *
 * Quarantine is the honest answer to "this lot is bad but we still have it".
 * FEFO skips a quarantined batch, so the stock stays on the balance sheet and
 * stops being sold — which is different from writing it off, and only one of the
 * two is reversible.
 */
function BatchEditor({
  batch,
  busy,
  onClose,
  onSave,
}: {
  batch: BatchRow;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [made, setMade] = useState(batch.manufactureDate ?? '');
  const [expiry, setExpiry] = useState(batch.expiryDate ?? '');
  const [supplierBatchNo, setSupplierBatchNo] = useState(batch.supplierBatchNo ?? '');
  const [note, setNote] = useState(batch.qualityNote ?? '');
  const [quarantined, setQuarantined] = useState(batch.isQuarantined);

  // The database's own rule (`batches_dates_ck`), restated so it is caught
  // before the round trip rather than after it. The database is still the
  // authority; this only saves the operator a refusal.
  const badDates = made !== '' && expiry !== '' && expiry <= made;

  return (
    <Card flush className="mb-3.5" data-noprint="1">
      <CardHeader
        eyebrow={batch.batchNo}
        title={`${batch.sku} · ${batch.productName}`}
        subtitle={`Received ${batch.receivedDate ? formatDate(batch.receivedDate) : '—'}${
          batch.supplierName ? ` from ${batch.supplierName}` : ''
        } · ${formatQty(batch.balanceQty, 0)} Kg on hand in ${batch.warehouseName}`}
        actions={
          <>
            {batch.isQuarantined && <Badge tone="bad">quarantined</Badge>}
            <Button size="sm" onClick={onClose}>
              Close
            </Button>
          </>
        }
      />
      <CardBody className="flex flex-col gap-3.5">
        <Callout tone="info" title="These dates belong to this lot alone">
          The product&rsquo;s shelf life is only the default a receipt starts from. Every purchase
          of {batch.sku} mints its own batch with its own dates, so correcting them here changes
          what FEFO reaches for and what the expiry report counts — and nothing about the next
          delivery.
        </Callout>

        <FieldRow>
          <TextField label="Manufactured" type="date" value={made} onChange={setMade} />
          <TextField
            label="Expires"
            type="date"
            value={expiry}
            hint={badDates ? 'Must be after the manufacture date' : undefined}
            onChange={setExpiry}
          />
          <TextField
            label="Supplier's own lot no."
            value={supplierBatchNo}
            onChange={setSupplierBatchNo}
            placeholder="As printed on their paperwork"
          />
          <div className="flex items-end pb-1">
            <Checkbox
              checked={quarantined}
              onChange={(e) => setQuarantined(e.target.checked)}
              label="Quarantine this lot"
              description="FEFO skips it and a sale must name another batch. The stock stays on the books."
            />
          </div>
        </FieldRow>

        <TextField
          label="Quality note"
          value={note}
          onChange={setNote}
          placeholder="Why the dates changed, or what is wrong with the lot"
        />

        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={busy || badDates}
            onClick={() =>
              onSave({
                manufactureDate: made || null,
                expiryDate: expiry || null,
                supplierBatchNo: supplierBatchNo.trim() || null,
                qualityNote: note.trim() || null,
                isQuarantined: quarantined,
              })
            }
          >
            Save batch
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
